import { Check, Copy, Download, ExternalLink, Plus, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { fileDownloadUrl } from "@/lib/api";
import { formatBytes, timeUntil } from "@/lib/format";

export function copyText(text) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  const el = document.createElement("textarea");
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
  return Promise.resolve();
}

export function ShareLink({ url, testId = "share-link" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await copyText(url);
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-stretch gap-2">
      <input readOnly value={url} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2.5 font-mono text-xs text-indigo-700 outline-none sm:text-sm" aria-label="Share link" data-testid={`${testId}-input`} />
      <Button onClick={copy} className="shrink-0 rounded-xl bg-indigo-600 hover:bg-indigo-700" data-testid={`${testId}-copy`}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}<span className="ml-2 hidden sm:inline">{copied ? "Copied" : "Copy Link"}</span></Button>
    </div>
  );
}

export async function shareOrCopy(file) {
  if (navigator.share) {
    try {
      await navigator.share({ title: file.original_filename, url: file.share_url });
    } catch (error) {
      if (error?.name !== "AbortError") console.error("Web Share failed", error);
    }
    return;
  }
  await copyText(file.share_url);
  toast.success("Link copied to clipboard");
}

export function SuccessCard({ results, onReset }) {
  return (
    <div className="card animate-rise p-5 sm:p-8" data-testid="upload-success-card">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-600"><Check className="h-5 w-5" /></span>
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">{results.length > 1 ? `${results.length} files are ready.` : "Your file is ready."}</h2>
          <p className="text-sm text-slate-500">Anyone with the link can view and download.</p>
        </div>
      </div>
      <div className="mt-6 space-y-5">
        {results.map((f) => (
          <div key={f.public_id} className="space-y-3" data-testid={`success-file-${f.public_id}`}>
            {results.length > 1 && <p className="truncate text-sm font-medium text-slate-800">{f.original_filename} <span className="text-slate-400">· {formatBytes(f.size)}</span></p>}
            <ShareLink url={f.share_url} testId={`share-${f.public_id}`} />
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm" className="rounded-full"><a href={f.share_url} target="_blank" rel="noopener noreferrer" data-testid={`open-file-${f.public_id}`}><ExternalLink className="mr-1.5 h-4 w-4" />Open File</a></Button>
              <Button asChild variant="outline" size="sm" className="rounded-full"><a href={fileDownloadUrl(f.public_id)} data-testid={`download-file-${f.public_id}`}><Download className="mr-1.5 h-4 w-4" />Download</a></Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => shareOrCopy(f)} data-testid={`share-file-${f.public_id}`}><Share2 className="mr-1.5 h-4 w-4" />Share</Button>
              <span className="ml-auto self-center text-xs text-slate-400">{timeUntil(f.expires_at)}</span>
            </div>
          </div>
        ))}
      </div>
      <Button variant="ghost" onClick={onReset} className="mt-6 w-full rounded-xl text-slate-700 sm:w-auto" data-testid="upload-another-btn"><Plus className="mr-1.5 h-4 w-4" />Upload Another</Button>
    </div>
  );
}
